/**
 * RiderDeductionFilterSection Component
 * 협력사 선택 페이지 검색 필터 섹션
 */
import React from 'react';
import { SupplierFilters, OPERATION_STATUS_OPTIONS } from '../utils/supplierSelectionHelpers';
import '@/shared/styles/search-form.css';

/** 빠른 필터 프리셋 */
type QuickFilterPreset = 'all' | 'operating' | 'terminated';

interface FilterSectionProps {
    filters: SupplierFilters;
    onFilterChange: (filters: SupplierFilters) => void;
    onSearch: () => void;
    onQuickFilter: (filters: SupplierFilters) => void;
    onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    onUploadClick: () => void;
    onExcelDownload: () => void;
}

const FilterSection: React.FC<FilterSectionProps> = ({
    filters,
    onFilterChange,
    onSearch,
    onQuickFilter,
    onKeyDown,
    onUploadClick,
    onExcelDownload
}) => {
    /**
     * 빠른 필터 버튼 클릭 핸들러
     */
    const handleQuickFilter = (preset: QuickFilterPreset) => {
        let newStatus = '전체';
        switch (preset) {
            case 'operating':
                newStatus = '운영중';
                break;
            case 'terminated':
                newStatus = '계약종료';
                break;
            default:
                newStatus = '전체';
        }
        onQuickFilter({ ...filters, operationStatus: newStatus });
    };

    /**
     * 현재 활성화된 빠른 필터 확인
     */
    const getActivePreset = (): QuickFilterPreset => {
        if (filters.operationStatus === '운영중') return 'operating';
        if (filters.operationStatus === '계약종료') return 'terminated';
        return 'all';
    };

    const activePreset = getActivePreset();

    return (
        <div className="card search-card supplier-filter-card">
            <div className="card-body">
                {/* 빠른 필터 버튼 */}
                <div className="quick-filter-row">
                    <div className="quick-filter-buttons">
                        <button
                            type="button"
                            className={`quick-filter-btn ${activePreset === 'all' ? 'active' : ''}`}
                            onClick={() => handleQuickFilter('all')}
                        >
                            전체
                        </button>
                        <button
                            type="button"
                            className={`quick-filter-btn ${activePreset === 'operating' ? 'active' : ''}`}
                            onClick={() => handleQuickFilter('operating')}
                        >
                            운영중
                        </button>
                        <button
                            type="button"
                            className={`quick-filter-btn ${activePreset === 'terminated' ? 'active' : ''}`}
                            onClick={() => handleQuickFilter('terminated')}
                        >
                            계약종료
                        </button>
                    </div>
                </div>

                <div className="search-form supplier-search-form">
                    {/* 협력사명 */}
                    <div className="form-group">
                        <div className="form-field">
                            <label className="form-label">
                                <i className="fas fa-building" />
                                협력사명
                            </label>
                            <input
                                type="text"
                                className="text-input"
                                value={filters.supplierName}
                                onChange={(e) => onFilterChange({ ...filters, supplierName: e.target.value })}
                                onKeyDown={onKeyDown}
                                placeholder="협력사명 입력..."
                            />
                        </div>
                    </div>

                    {/* 협력사 아이디 */}
                    <div className="form-group">
                        <div className="form-field">
                            <label className="form-label">
                                <i className="fas fa-id-card" />
                                협력사 아이디
                            </label>
                            <input
                                type="text"
                                className="text-input"
                                value={filters.supplierId}
                                onChange={(e) => onFilterChange({ ...filters, supplierId: e.target.value })}
                                onKeyDown={onKeyDown}
                                placeholder="아이디 입력..."
                            />
                        </div>
                    </div>

                    {/* 운영 상태 */}
                    <div className="form-group">
                        <div className="form-field">
                            <label className="form-label">
                                <i className="fas fa-toggle-on" />
                                운영 상태
                            </label>
                            <div className="status-select-wrapper">
                                <select
                                    className="status-select"
                                    value={filters.operationStatus}
                                    onChange={(e) => onFilterChange({ ...filters, operationStatus: e.target.value })}
                                >
                                    {OPERATION_STATUS_OPTIONS.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* 버튼 그룹 */}
                    <div className="form-group button-group">
                        <button className="btn btn-secondary" onClick={onUploadClick}>
                            <i className="fas fa-upload" />
                            등급파일
                        </button>
                        <button className="btn btn-success" onClick={onExcelDownload}>
                            <i className="fas fa-file-excel" />
                            엑셀
                        </button>
                        <button className="btn btn-primary" onClick={onSearch}>
                            <i className="fas fa-search" />
                            조회
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FilterSection;
